import type { AxiosRequestConfig } from 'axios'
import merge from 'lodash.merge'
import throttle from 'lodash.throttle'
import queryString from 'query-string'

import type {
  CableHistoryId,
  CableHistoryInitializer,
  CableHistoryMutator,
  CableId,
} from '@beaded/models'

import { API_BASE_URI } from 'constants/api'

import type { Obj, ID } from 'types/common'

import * as apiUtils from '.'

const url = '/cable'

const headers = {
  'x-api-version': '1.1',
}

export const get = (id: ID, options: AxiosRequestConfig = {}) =>
  apiUtils.GET(`${url}/${id}`, merge({}, { headers }, options))

export const getAll = (options: AxiosRequestConfig = {}) =>
  apiUtils.GET(`${url}/`, merge({}, { headers }, options))

export const getBySerialNumber = (
  serial: ID,
  options: AxiosRequestConfig = {},
) => apiUtils.GET(`${url}/serial/${serial}`, merge({}, { headers }, options))

export const create = (data: Obj, options: AxiosRequestConfig = {}) =>
  apiUtils.POST(`${url}/`, data, merge({}, { headers }, options))

export const createEvent = (
  id: ID,
  data: Obj,
  options: AxiosRequestConfig = {},
) => apiUtils.PUT(`${url}/${id}/event`, data, merge({}, { headers }, options))

export const createUse = (
  id: ID,
  data: Obj,
  options: AxiosRequestConfig = {},
) => apiUtils.PUT(`${url}/${id}/use`, data, merge({}, { headers }, options))

// Cable history methods
export const createHistory = (
  cableId: CableId,
  data: CableHistoryInitializer,
  options: AxiosRequestConfig = {},
) =>
  apiUtils.POST(
    `${url}/${cableId}/history`,
    data,
    merge({}, { headers }, options),
  )

export const bulkUpdateHistory = (
  cableId: CableId,
  data: CableHistoryMutator[],
  options: AxiosRequestConfig = {},
) =>
  apiUtils.PUT(
    `${url}/${cableId}/history`,
    data,
    merge({}, { headers }, options),
  )

export const updateHistory = (
  cableId: CableId,
  cableHistoryId: CableHistoryId,
  data: CableHistoryMutator,
  options: AxiosRequestConfig = {},
) =>
  apiUtils.PUT(
    `${url}/${cableId}/history/${cableHistoryId}`,
    data,
    merge({}, { headers }, options),
  )

export const removeHistory = (
  cableId: CableId,
  cableHistoryId: CableHistoryId,
  options: AxiosRequestConfig = {},
) =>
  apiUtils.DELETE(
    `${url}/${cableId}/history/${cableHistoryId}`,
    merge({}, { headers }, options),
  )

export const getPrice = throttle(
  (bom: Obj) => apiUtils.POST(`${url}/price`, bom),
  500,
)

export const update = (id: ID, data: Obj, options: AxiosRequestConfig = {}) =>
  apiUtils.PUT(`${url}/${id}`, data, merge({}, { headers }, options))

export const patch = (id: ID, data: Obj, options: AxiosRequestConfig = {}) =>
  apiUtils.PATCH(`${url}/${id}`, data, merge({}, { headers }, options))

export const remove = (id: ID, options: AxiosRequestConfig = {}) =>
  apiUtils.DELETE(`${url}/${id}`, merge({}, { headers }, options))

export const getMenuProjection = (options: AxiosRequestConfig = {}) =>
  apiUtils.GET(
    `${url}/`,
    merge({ params: { projection: 'menu' } }, merge({}, { headers }, options)),
  )

export const createBomInErpNext = (id: ID, options: AxiosRequestConfig = {}) =>
  apiUtils.PUT(`${url}/${id}/erpnext-sync`, {}, merge({}, options, { headers }))

export const lockCable = (
  id: ID,
  body: { syncBom?: boolean } = {},
  options: AxiosRequestConfig = {},
) => apiUtils.POST(`${url}/${id}/lock`, body, merge({}, options, { headers }))

export const downloadMultipleCutterXml = (
  id: ID[],
  options: AxiosRequestConfig = {},
) => {
  const params = merge({}, options, { headers })
  for (const cableId of id) {
    window.open(
      `${API_BASE_URI()}${url}/${cableId}/cutter?${queryString.stringify(
        params.params,
      )}`,
      '_blank',
    )
  }
}

export const downloadCutterXml = (id: ID, options: AxiosRequestConfig = {}) => {
  const params = merge({}, options, { headers })
  window.open(
    `${API_BASE_URI()}${url}/${id}/cutter?${queryString.stringify(
      params.params,
    )}`,
    '_blank',
  )
}
